/***************************************************************************************************
* This file configures the Redux store for the application, combining multiple reducers.
* 
* It allows:
* 
* 1. Integration of various feature reducers, such as authentication, users, UI, products, templates, etc.
* 2. Centralized state management, making the store accessible throughout the entire application.
* 3. Each slice manages its own part of the state, ensuring scalability and separation of concerns.
* 
* The store is set up using Redux Toolkit's `configureStore` for easier configuration and middleware integration.
***************************************************************************************************/

import { configureStore } from '@reduxjs/toolkit'

import { authReducer } from './auth.slice'
import { usersReducer } from './users.slice'
import { uiReducer } from './ui.slice'
import { productsReducer } from './products.slice'
import { templatesReducer } from './templates.slice'
import { projectsReducer } from './projects.slice'
import { stepsReducer } from './steps.slice'
import { quotationsReducer } from './quotations.slice'
import { abeQuotationsReducer } from '../ABE_files/ABE_Quotations.slice';
import { abe_10_40QuotationsReducer } from '../ABE_10_40_files/ABE_10_40_Quotations.slice';
import { sdmeQuotationsReducer } from '../SDME_files/SDME_Quotations.slice';
import { sdmQuotationsReducer } from '../SDM_files/SDM_Quotations.slice';
import { ldeQuotationsReducer } from '../LDE_files/LDE_Quotations.slice';
import { dtheQuotationsReducer } from '../DTHE_files/DTHE_Quotations.slice';
import { superblockQuotationsReducer } from '../Superblock_files/Superblock_Quotations.slice';
import { submittalsReducer } from './submittals.slice'
import { abeSubmittalsReducer } from '../ABE_files/ABE_Submittals.slice';
import { abe_10_40SubmittalsReducer } from '../ABE_10_40_files/ABE_10_40_Submittals.slice';
import { sdmeSubmittalsReducer } from '../SDME_files/SDME_Submittals.slice';
import { sdmSubmittalsReducer } from '../SDM_files/SDM_Submittals.slice';
import { ldeSubmittalsReducer } from '../LDE_files/LDE_Submittals.slice';
import { dtheSubmittalsReducer } from '../DTHE_files/DTHE_Submittals.slice';
import { superblockSubmittalsReducer } from '../Superblock_files/Superblock_Submittals.slice';
import { superblockSelectionsReducer } from 'src/Superblock_files/Superblock_Selections.slice'
import { revisionsReducer } from './revisions.slice'
import { parametersReducer } from './parameters.slice'
import { notificationsReducer } from './notifications.slice'
import { companiesReducer } from './companies.slice'
import { officesReducer } from './offices.slice'
import { locationsReducer } from './locations.slice'

export * from './auth.slice'
export * from './users.slice'
export * from './ui.slice'
export * from './templates.slice'
export * from './products.slice'
export * from './projects.slice'
export * from './steps.slice'
export * from './quotations.slice'
export * from '../ABE_files/ABE_Quotations.slice';
export * from '../ABE_10_40_files/ABE_10_40_Quotations.slice';
export * from '../SDME_files/SDME_Quotations.slice';
export * from '../SDM_files/SDM_Quotations.slice';
export * from '../LDE_files/LDE_Quotations.slice';
export * from '../DTHE_files/DTHE_Quotations.slice';
export * from '../Superblock_files/Superblock_Quotations.slice';
export * from '../ABE_files/ABE_Submittals.slice';
export * from '../ABE_10_40_files/ABE_10_40_Submittals.slice';
export * from '../SDME_files/SDME_Submittals.slice';
export * from '../SDM_files/SDM_Submittals.slice';
export * from '../LDE_files/LDE_Submittals.slice';
export * from '../DTHE_files/DTHE_Submittals.slice';
export * from '../Superblock_files/Superblock_Submittals.slice';
export * from '../Superblock_files/Superblock_Selections.slice';
export * from './submittals.slice'
export * from './parameters.slice'
export * from './revisions.slice'
export * from './companies.slice'
export * from './offices.slice'
export * from './locations.slice'

export const store = configureStore({
  reducer: {
    auth: authReducer,
    users: usersReducer,
    ui: uiReducer,
    templates: templatesReducer,
    projects: projectsReducer,
    products: productsReducer,
    steps: stepsReducer,
    quotations: quotationsReducer,
    abeQuotations: abeQuotationsReducer,
    abe_10_40Quotations: abe_10_40QuotationsReducer,
    sdmeQuotations: sdmeQuotationsReducer,
    sdmQuotations: sdmQuotationsReducer,
    ldeQuotations: ldeQuotationsReducer,
    dtheQuotations: dtheQuotationsReducer,
    superblockQuotations: superblockQuotationsReducer,
    submittals: submittalsReducer,
    abeSubmittals: abeSubmittalsReducer,
    abe_10_40Submittals: abe_10_40SubmittalsReducer,
    sdmeSubmittals: sdmeSubmittalsReducer,
    sdmSubmittals: sdmSubmittalsReducer,
    ldeSubmittals: ldeSubmittalsReducer,
    dtheSubmittals: dtheSubmittalsReducer,
    superblockSubmittals: superblockSubmittalsReducer,
    superblockSelections: superblockSelectionsReducer,
    revisions: revisionsReducer,
    notifications: notificationsReducer,
    parameters: parametersReducer,
    companies: companiesReducer,
    offices: officesReducer,
    locations: locationsReducer,
  },
})
